/**
 * RobotPicture — a robot's picture at any size, with a stable fallback chain.
 *
 * Rendered anywhere a robot is named: the My Robots grid, table rows, the
 * details header. Resolution order: the robot's own uploaded picture -> its
 * bot type's default picture -> initials. Because both pictures come from
 * presigned URLs that can expire or 404, the fallback is not an edge case —
 * it is the normal state for any robot that hasn't had one uploaded, and it
 * must look deliberate. Mirrors botriq-admin-fe's RobotPicture.
 */
import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Avatar } from "@mui/material";
import { useRobotPictures } from "../../context/RobotPictureContext";
import { useBotTypePictures } from "../../context/BotTypePictureContext";
import { tokens } from "../../theme";

const avatarColor = (name = "") => {
  const code = name.slice(0, 2).toUpperCase() || "RB";
  const hue = (code.charCodeAt(0) * 37 + (code.charCodeAt(1) || 0) * 11) % 360;
  return { code, color: `hsl(${hue} 55% 45%)` };
};

const RobotPicture = ({ robotId, botTypeId, name, size = 36, radius = 10, src }) => {
  const { pictures } = useRobotPictures();
  const { botTypePictures } = useBotTypePictures();
  // Priority: an explicit `src` > the robot's own picture > its bot type's
  // default picture.
  const url =
    src ||
    (robotId ? pictures[String(robotId)] : null) ||
    (botTypeId ? botTypePictures[String(botTypeId)] : null);

  const [failed, setFailed] = useState(false);
  // A new URL deserves a fresh attempt — without this, one expired URL would
  // keep the fallback pinned even after the interval signed a working one.
  useEffect(() => {
    setFailed(false);
  }, [url]);

  // Attached imperatively rather than via a JSX onError prop (sonar javascript:S6847).
  const attachErrorHandler = useCallback((node) => {
    if (!node) return;
    node.onerror = () => setFailed(true);
  }, []);

  if (!url || failed) {
    const { code, color } = avatarColor(name);
    return (
      <Avatar
        sx={{
          width: size,
          height: size,
          borderRadius: `${radius}px`,
          background: color,
          fontSize: Math.max(10, size * 0.36),
          fontWeight: 700,
        }}
      >
        {code}
      </Avatar>
    );
  }

  return (
    <img
      ref={attachErrorHandler}
      src={url}
      alt={name ? `${name} picture` : "Robot picture"}
      width={size}
      height={size}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        flex: "none",
        objectFit: "contain",
        background: tokens.surface3,
      }}
    />
  );
};

RobotPicture.propTypes = {
  robotId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** The robot's bot type id (robot.deviceType._id) — used for the default-image fallback. */
  botTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  size: PropTypes.number,
  radius: PropTypes.number,
  /** Overrides the looked-up URL. */
  src: PropTypes.string,
};

export default RobotPicture;
